import { WebLocale } from '@/enums/web-locale';
import { RouteConfig } from 'vue-router/types/router';
import { CollectionType } from '@/enums/collection-type';

/**
 * Generate the app routes for Authors, List Articles, and
 * News Articles.
 *
 * @param currentLocale
 */
export function makeEditorialRoutes(currentLocale: WebLocale) {
	/*
        **IMPORTANT**
        The route order is important, they are evaluated in order so
        watchout for overlapping :dyanmic route segments!
    */

	const routes: RouteConfig[] = [];

	if (isEditorialLocale(currentLocale)) {
		routes.push(makeGuideRoute(currentLocale));
		routes.push(makeEditorialArticleRoute(currentLocale));
	}

	if (isAuthorLocale(currentLocale)) {
		routes.push(makeAuthorRoute(currentLocale));
	}

	return routes;
}

/////////////
/* AUTHORS */
/////////////
type AuthorLocale = keyof typeof authorSlugs;

const authorSlugs = {
	[WebLocale.au]: 'author',
	[WebLocale.ca]: 'author',
	[WebLocale.de]: 'autor',
	[WebLocale.es]: 'autor',
	[WebLocale.fr]: 'auteur',
	[WebLocale.uk]: 'author',
	[WebLocale.us]: 'author',
} as const;

export function makeAuthorRoute(currentLocale: AuthorLocale): RouteConfig {
	return {
		path: `/${currentLocale}/${authorSlugs[currentLocale]}/:name`,
		name: 'app.landingpage.user',
		component: () => import(/* webpackChunkName: "authorpage" */ '@/pages/authors/AuthorPage.vue'),
		meta: { tab: 'landingpage', data: 'userpage' },
	};
}

function isAuthorLocale(locale: WebLocale): locale is AuthorLocale {
	return locale in authorSlugs;
}

////////////////////
/* GUIDE HOMEPAGE */
////////////////////
export type EditorialLocale = keyof typeof editorialSlugs;

export const editorialSlugs = {
	[WebLocale.de]: 'guide',
	[WebLocale.es]: 'guia',
	[WebLocale.fr]: 'guide',
	[WebLocale.it]: 'guida',
	[WebLocale.uk]: 'guide',
	[WebLocale.us]: 'guide',
} as const;

export function isEditorialLocale(locale: WebLocale): locale is EditorialLocale {
	return locale in editorialSlugs;
}

export function makeGuideRoute(currentLocale: EditorialLocale): RouteConfig {
	return {
		path: `/${currentLocale}/${editorialSlugs[currentLocale]}`,
		name: 'app.editorial.list',
		component: () => import(/* webpackChunkName: "editorial" */ '@/pages/editorial/GuidePage.vue'),
		meta: { tab: 'editorial' },
	};
}

////////////////////////
/* EDITORIAL ARTICLES */
////////////////////////
function makeEditorialArticleRoute(currentLocale: EditorialLocale): RouteConfig {
	// /{locale}/{guide} -> /de/news/:slug
	return {
		component: () => import(/* webpackChunkName: "editorial" */ '@/pages/editorial/EditorialArticleWrapper.vue'),
		name: 'app.editorial.article',
		path: `/${currentLocale}/${editorialSlugs[currentLocale]}/:slug`,
		meta: { tab: 'editorial', collectionType: CollectionType.EDITORIAL, context: ['filters'] },
	};
}
